import { format as formatDate, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
  FormControl,
} from "../form";
import { Popover, PopoverTrigger, PopoverContent } from "../popover";
import { Calendar } from "../calendar";
import { Input } from "../input";
import { Button } from "../button";
import { cn } from "../../lib/utils";

export function FormDatePicker({
  value,
  onChange,
  format = "yyyy-MM-dd",
  description,
  label,
  disabled,
  fieldDisabled,
  placeholder,
  yearRange,
  className,
}: {
  value?: string;
  onChange: (date: string) => void;
  format?: string;
  description?: string;
  label: string;
  disabled?: (date: Date) => boolean;
  fieldDisabled?: boolean;
  placeholder?: string;
  yearRange?: {
    from: number;
    to: number;
  };
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [isIncomplete, setIsIncomplete] = useState(false);

  const handleChange = (date?: Date) => {
    if (!date) return;
    const formattedDate = formatDate(date, format);
    onChange(formattedDate);
    setIsIncomplete(false);
    setOpen(false);
  };

  const date = useMemo(
    () => (value ? parse(value, format, new Date()) : undefined),
    [value, format]
  );
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <FormItem className={cn("flex-1 w-full", className)}>
      <FormLabel>{label}</FormLabel>
      <Popover modal={true} open={open} onOpenChange={setOpen}>
        <FormControl>
          <div className="relative h-9 flex w-full">
            <Input
              ref={inputRef}
              className="w-full px-3"
              type={value ? "date" : "text"}
              value={value}
              aria-invalid={isIncomplete || undefined}
              onChange={(e) => {
                if (!e.target.validity.badInput) setIsIncomplete(false);
                onChange(e.target.value);
              }}
              // Replace the browser's validation popup with our own message
              onInvalid={(e) => {
                e.preventDefault();
                setIsIncomplete(true);
                e.currentTarget.focus();
              }}
              onFocus={() => {
                if (!inputRef.current) return;
                inputRef.current.type = "date";
              }}
              onBlur={(e) => {
                if (!inputRef.current) return;
                // A partially typed date reports value "" but badInput; keep
                // the date type so switching to text doesn't wipe it.
                const { value: inputValue, validity } = e.currentTarget;
                inputRef.current.type =
                  inputValue || validity.badInput ? "date" : "text";
              }}
              placeholder={placeholder || "Pick a date"}
              disabled={fieldDisabled}
            />
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={fieldDisabled}
                className="absolute hover:bg-secondary/50 rounded-l-none right-0 top-1/2 -translate-y-1/2"
              >
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
          </div>
        </FormControl>
        <PopoverContent
          side="top"
          autoFocus={false}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="w-auto p-0"
          align="end"
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleChange}
            disabled={disabled}
            yearRange={yearRange}
          />
        </PopoverContent>
      </Popover>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage>{isIncomplete ? "Enter a valid date" : null}</FormMessage>
    </FormItem>
  );
}

export function DatePicker({
  placeholder,
  value,
  onChange,
  disabled,
  format = "yyyy-MM-dd",
  yearRange,
  options,
}: {
  placeholder?: string;
  value?: string;
  onChange: (date: string) => void;
  disabled?: (date: Date) => boolean;
  format?: string;
  yearRange?: {
    from: number;
    to: number;
  };
  options?: {
    portal?: boolean;
    modal?: boolean;
    side?: "top" | "right" | "left" | "bottom";
  };
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);

  const handleNativeChange = (date?: Date) => {
    if (!date) return;
    const formattedDate = formatDate(date, format);
    onChange(formattedDate);
    setOpen(false);
  };

  const date = useMemo(
    () => (value ? parse(value, format, new Date()) : undefined),
    [value, format]
  );

  return (
    <Popover modal={options?.modal ?? true} open={open} onOpenChange={setOpen}>
      <div className="relative h-9 flex w-full">
        <Input
          ref={inputRef}
          className="w-full px-3"
          type={value ? "date" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            if (!inputRef.current) return;
            inputRef.current.type = "date";
          }}
          onBlur={(e) => {
            if (!inputRef.current) return;
            inputRef.current.type = e.currentTarget.value ? "date" : "text";
          }}
          placeholder={placeholder || "Pick a date"}
        />
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute hover:bg-secondary/50 rounded-l-none right-0 top-1/2 -translate-y-1/2"
          >
            <CalendarIcon />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        side={options?.side ?? "top"}
        autoFocus={false}
        portal={options?.portal ?? true}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-auto p-0"
        align="end"
      >
        <Calendar
          mode="single"
          disabled={disabled}
          selected={date}
          onSelect={handleNativeChange}
          yearRange={yearRange}
        />
      </PopoverContent>
    </Popover>
  );
}
